(() => {
  const galleryImageCache = window.gigiGalleryImageCache || (window.gigiGalleryImageCache = new Map());
  const galleryImagesReady = window.gigiGalleryImagesReady || (window.gigiGalleryImagesReady = new Set());
  const GALLERY_SLIDE_MS = 420;
  const MOBILE_QUERY = '(max-width: 749px)';
  const SWIPE_MIN_DISTANCE = 42;
  const SWIPE_AXIS_RATIO = 1.25;
  const SWIPE_START_DISTANCE = 8;
  const INGREDIENTS_DIALOG_MS = 220;

  function prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
  }

  function getGallerySlideDirection(prevIndex, nextIndex, count) {
    if (prevIndex === nextIndex) return 0;
    if (nextIndex === prevIndex + 1) return 1;
    if (nextIndex === prevIndex - 1) return -1;
    if (prevIndex === count - 1 && nextIndex === 0) return 1;
    if (prevIndex === 0 && nextIndex === count - 1) return -1;
    return nextIndex > prevIndex ? 1 : -1;
  }

  function preloadGalleryImage(src) {
    if (!src) return Promise.resolve();
    if (galleryImagesReady.has(src)) return Promise.resolve();
    if (galleryImageCache.has(src)) return galleryImageCache.get(src);

    const promise = new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        galleryImagesReady.add(src);
        resolve(img);
      };
      img.onerror = () => resolve(img);
      img.src = src;
    });

    galleryImageCache.set(src, promise);
    return promise;
  }

  function applyGalleryImage(mainImage, thumb, fullSrc) {
    mainImage.removeAttribute('srcset');
    mainImage.removeAttribute('sizes');
    mainImage.src = fullSrc;
    mainImage.dataset.mediaSrc = fullSrc;

    if (thumb.dataset.alt) {
      mainImage.alt = thumb.dataset.alt;
    }

    mainImage.classList.remove('gfs-panel__main-img--loading');
    mainImage.closest('[data-gfs-gallery-stage]')?.classList.add('is-gfs-image-loaded');
  }

  function markGalleryStageImageState(stage) {
    if (!stage) return;

    const image = stage.querySelector('.gfs-panel__main-img:not(.gfs-panel__main-img--slide-incoming)');
    if (!image) return;

    const markLoaded = () => {
      stage.classList.add('is-gfs-image-loaded');
    };

    if (image.complete && image.naturalWidth > 0) {
      markLoaded();
      return;
    }

    image.addEventListener('load', markLoaded, { once: true });
    image.addEventListener('error', markLoaded, { once: true });
  }

  function slideGalleryImage(gallery, mainImage, thumb, fullSrc, direction) {
    if (direction === 0 || prefersReducedMotion()) {
      applyGalleryImage(mainImage, thumb, fullSrc);
      return;
    }

    const stage = gallery.querySelector('[data-gfs-gallery-stage]');
    if (!stage || gallery.dataset.gfsGallerySliding === 'true') {
      applyGalleryImage(mainImage, thumb, fullSrc);
      return;
    }

    gallery.dataset.gfsGallerySliding = 'true';

    const imageRect = mainImage.getBoundingClientRect();
    stage.style.width = `${imageRect.width}px`;
    stage.style.height = `${imageRect.height}px`;

    const outgoing = mainImage;
    outgoing.classList.add('gfs-panel__main-img--slide');
    outgoing.style.transform = 'translateX(0)';

    const incoming = document.createElement('img');
    incoming.className = 'gfs-panel__main-img gfs-panel__main-img--slide gfs-panel__main-img--slide-incoming';
    incoming.src = fullSrc;
    incoming.alt = thumb.dataset.alt || outgoing.alt || '';
    incoming.dataset.mediaSrc = fullSrc;
    if (outgoing.width) incoming.width = outgoing.width;
    if (outgoing.height) incoming.height = outgoing.height;
    incoming.loading = 'eager';
    incoming.style.transform = `translateX(${direction * 100}%)`;

    stage.appendChild(incoming);
    incoming.offsetHeight;

    requestAnimationFrame(() => {
      outgoing.style.transform = `translateX(${direction * -100}%)`;
      incoming.style.transform = 'translateX(0)';
    });

    let finished = false;

    function finishSlide() {
      if (finished) return;
      finished = true;

      incoming.classList.remove('gfs-panel__main-img--slide', 'gfs-panel__main-img--slide-incoming');
      incoming.style.transform = '';
      incoming.style.transition = '';
      outgoing.remove();
      stage.style.width = '';
      stage.style.height = '';
      delete gallery.dataset.gfsGallerySliding;
      applyGalleryImage(incoming, thumb, fullSrc);
    }

    incoming.addEventListener('transitionend', finishSlide, { once: true });
    window.setTimeout(finishSlide, GALLERY_SLIDE_MS + 60);
  }

  function markGalleryThumbs(gallery, activeThumb) {
    gallery.querySelectorAll('[data-gfs-gallery-thumb]').forEach((button) => {
      button.setAttribute('aria-current', button === activeThumb ? 'true' : 'false');
    });

    const thumbs = [...gallery.querySelectorAll('[data-gfs-gallery-thumb]')];
    const index = thumbs.indexOf(activeThumb);

    gallery.dispatchEvent(
      new CustomEvent('gigiGallerySlideChanged', {
        detail: { index: index >= 0 ? index : 0 },
      })
    );
  }

  function getActiveGalleryThumbIndex(gallery) {
    const thumbs = [...gallery.querySelectorAll('[data-gfs-gallery-thumb]')];
    const activeThumb = gallery.querySelector('[data-gfs-gallery-thumb][aria-current="true"]');
    const activeIndex = thumbs.indexOf(activeThumb);

    return activeIndex >= 0 ? activeIndex : 0;
  }

  function activateGalleryThumb(thumb) {
    if (!thumb) return;

    const gallery = thumb.closest('[data-gfs-gallery]');
    if (!gallery || gallery.dataset.gfsGallerySliding === 'true') return;

    const mainImage = gallery.querySelector('.gfs-panel__main-img');
    const fullSrc = thumb.dataset.mediaSrc;
    const previewSrc = thumb.dataset.thumbSrc;
    if (!mainImage || !fullSrc) return;

    const thumbs = [...gallery.querySelectorAll('[data-gfs-gallery-thumb]')];
    const nextIndex = thumbs.indexOf(thumb);
    const prevIndex = Number.parseInt(gallery.dataset.gfsActiveIndex || '0', 10);
    const direction = getGallerySlideDirection(prevIndex, nextIndex, thumbs.length);

    markGalleryThumbs(gallery, thumb);
    gallery.dataset.gfsActiveIndex = String(nextIndex);

    if (mainImage.dataset.mediaSrc === fullSrc && !mainImage.classList.contains('gfs-panel__main-img--loading')) {
      return;
    }

    const revealFullImage = () => slideGalleryImage(gallery, mainImage, thumb, fullSrc, direction);

    if (galleryImagesReady.has(fullSrc)) {
      revealFullImage();
      return;
    }

    if (previewSrc) {
      mainImage.classList.add('gfs-panel__main-img--loading');
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = previewSrc;
    }

    preloadGalleryImage(fullSrc).then(() => {
      const activeThumb = gallery.querySelector('[data-gfs-gallery-thumb][aria-current="true"]');
      if (activeThumb === thumb) revealFullImage();
    });
  }

  function getGallerySwipeTarget(gallery, deltaX) {
    const thumbs = [...gallery.querySelectorAll('[data-gfs-gallery-thumb]')];
    if (thumbs.length < 2) return null;

    const direction = deltaX < 0 ? 1 : -1;
    const activeIndex = getActiveGalleryThumbIndex(gallery);
    const targetIndex = ((activeIndex + direction) % thumbs.length + thumbs.length) % thumbs.length;
    const targetThumb = thumbs[targetIndex];

    if (!targetThumb?.dataset.mediaSrc) return null;

    return {
      direction,
      thumb: targetThumb,
      index: targetIndex,
    };
  }

  function clearGalleryDragState(state) {
    if (!state) return;

    state.incoming.remove();
    state.outgoing.classList.remove('gfs-panel__main-img--slide');
    state.outgoing.style.transform = '';
    state.outgoing.style.transition = '';
    state.stage.style.width = '';
    state.stage.style.height = '';
    delete state.gallery.dataset.gfsGalleryDragging;
    state.gallery.classList.remove('is-carousel-hovering');
  }

  function setGalleryDragTransforms(state, deltaX) {
    const clampedDelta = Math.max(-state.width, Math.min(state.width, deltaX));

    state.outgoing.style.transform = `translateX(${clampedDelta}px)`;
    state.incoming.style.transform = `translateX(${state.direction * state.width + clampedDelta}px)`;
  }

  function createGalleryDragState(gallery, deltaX) {
    if (!gallery || gallery.dataset.gfsGallerySliding === 'true') return null;

    const target = getGallerySwipeTarget(gallery, deltaX);
    const stage = gallery.querySelector('[data-gfs-gallery-stage]');
    const mainImage = gallery.querySelector('.gfs-panel__main-img:not(.gfs-panel__main-img--slide-incoming)');
    if (!target || !stage || !mainImage || mainImage.classList.contains('gfs-panel__main-img--loading')) return null;

    const imageRect = mainImage.getBoundingClientRect();
    const width = imageRect.width || stage.getBoundingClientRect().width;
    const height = imageRect.height || stage.getBoundingClientRect().height;
    if (!width || !height) return null;

    gallery.dataset.gfsGalleryDragging = 'true';
    gallery.classList.add('is-carousel-hovering');
    stage.style.width = `${width}px`;
    stage.style.height = `${height}px`;

    mainImage.classList.add('gfs-panel__main-img--slide');
    mainImage.style.transition = 'none';
    mainImage.style.transform = 'translateX(0)';

    const fullSrc = target.thumb.dataset.mediaSrc;
    const dragSrc = galleryImagesReady.has(fullSrc) ? fullSrc : target.thumb.dataset.thumbSrc || fullSrc;
    const incoming = document.createElement('img');
    incoming.className = 'gfs-panel__main-img gfs-panel__main-img--slide gfs-panel__main-img--slide-incoming';
    incoming.src = dragSrc;
    incoming.alt = target.thumb.dataset.alt || mainImage.alt || '';
    incoming.dataset.mediaSrc = fullSrc;
    incoming.loading = 'eager';
    if (mainImage.width) incoming.width = mainImage.width;
    if (mainImage.height) incoming.height = mainImage.height;
    incoming.style.transition = 'none';
    incoming.style.transform = `translateX(${target.direction * width}px)`;

    stage.appendChild(incoming);
    preloadGalleryImage(fullSrc).then(() => {
      if (incoming.isConnected && incoming.src !== fullSrc) incoming.src = fullSrc;
    });

    return {
      ...target,
      gallery,
      stage,
      outgoing: mainImage,
      incoming,
      width,
    };
  }

  function finishGalleryDrag(state, shouldCommit) {
    if (!state) return;

    state.outgoing.style.transition = '';
    state.incoming.style.transition = '';
    state.incoming.offsetHeight;

    let finished = false;

    function finish() {
      if (finished) return;
      finished = true;

      if (shouldCommit) {
        state.incoming.classList.remove('gfs-panel__main-img--slide', 'gfs-panel__main-img--slide-incoming');
        state.incoming.style.transform = '';
        state.incoming.style.transition = '';
        state.outgoing.remove();
        state.stage.style.width = '';
        state.stage.style.height = '';
        delete state.gallery.dataset.gfsGallerySliding;
        delete state.gallery.dataset.gfsGalleryDragging;
        state.gallery.classList.remove('is-carousel-hovering');
        applyGalleryImage(state.incoming, state.thumb, state.thumb.dataset.mediaSrc);
        state.gallery.dispatchEvent(
          new CustomEvent('gigiGalleryUserNavigated', {
            detail: { index: state.index, source: 'swipe' },
          })
        );
        state.gallery.querySelector('carousel-loading-bar')?.restartAutoplay?.(state.index);
        return;
      }

      clearGalleryDragState(state);
    }

    if (shouldCommit) {
      state.gallery.dataset.gfsGallerySliding = 'true';
      markGalleryThumbs(state.gallery, state.thumb);
      state.gallery.dataset.gfsActiveIndex = String(state.index);
      state.outgoing.style.transform = `translateX(${state.direction * -state.width}px)`;
      state.incoming.style.transform = 'translateX(0)';
    } else {
      state.outgoing.style.transform = 'translateX(0)';
      state.incoming.style.transform = `translateX(${state.direction * state.width}px)`;
    }

    state.incoming.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, GALLERY_SLIDE_MS + 80);
  }

  function isGallerySwipeEnabled(gallery) {
    const clickDragEnabled = gallery?.dataset.gfsClickDrag === 'true';
    const mobileSwipeEnabled =
      gallery?.classList.contains('gfs-panel__gallery--with-ingredients') &&
      window.matchMedia?.(MOBILE_QUERY)?.matches === true;

    return (
      (clickDragEnabled || mobileSwipeEnabled) &&
      gallery.querySelectorAll('[data-gfs-gallery-thumb]').length > 1
    );
  }

  function isInteractiveGallerySwipeTarget(target) {
    return Boolean(
      target?.closest?.('a, button, input, select, textarea, summary, [data-gigi-card-zoom], carousel-loading-bar')
    );
  }

  function bindGallerySwipe(gallery) {
    if (!gallery || gallery.dataset.gfsSwipeBound === 'true' || !window.PointerEvent) return;

    const stage = gallery.querySelector('[data-gfs-gallery-stage]');
    if (!stage) return;

    gallery.dataset.gfsSwipeBound = 'true';

    let startX = 0;
    let startY = 0;
    let pointerId = null;
    let tracking = false;
    let horizontalSwipe = false;
    let didDrag = false;
    let dragState = null;
    let documentSwipeBound = false;
    let suppressClick = false;
    let suppressClickTimer = null;

    function bindDocumentSwipeEvents() {
      if (documentSwipeBound) return;

      documentSwipeBound = true;
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerCancel);
      window.addEventListener('blur', onPointerCancel);
    }

    function unbindDocumentSwipeEvents() {
      if (!documentSwipeBound) return;

      documentSwipeBound = false;
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('blur', onPointerCancel);
    }

    function releasePointerCapture() {
      if (!stage.releasePointerCapture || pointerId === null) return;

      try {
        stage.releasePointerCapture(pointerId);
      } catch (error) {
        // Capture may already be released after a browser-level gesture.
      }
    }

    function suppressNextClick() {
      suppressClick = true;
      window.clearTimeout(suppressClickTimer);
      suppressClickTimer = window.setTimeout(() => {
        suppressClick = false;
      }, GALLERY_SLIDE_MS + 120);
    }

    function resetSwipe() {
      unbindDocumentSwipeEvents();
      startX = 0;
      startY = 0;
      pointerId = null;
      tracking = false;
      horizontalSwipe = false;
      didDrag = false;
      dragState = null;
    }

    function onPointerDown(event) {
      if (!isGallerySwipeEnabled(gallery)) return;
      if (event.button > 0) return;
      if (isInteractiveGallerySwipeTarget(event.target)) return;
      if (gallery.dataset.gfsGallerySliding === 'true' || gallery.dataset.gfsGalleryDragging === 'true') return;

      startX = event.clientX;
      startY = event.clientY;
      pointerId = event.pointerId;
      tracking = true;
      horizontalSwipe = false;
      didDrag = false;
      dragState = null;
      bindDocumentSwipeEvents();

      try {
        stage.setPointerCapture?.(pointerId);
      } catch (error) {
        // Browser may already own the gesture.
      }
    }

    function onPointerMove(event) {
      if (!tracking || event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      const target = absX > SWIPE_START_DISTANCE ? getGallerySwipeTarget(gallery, deltaX) : null;

      if (!horizontalSwipe && target && absX > SWIPE_START_DISTANCE && absX > absY * SWIPE_AXIS_RATIO) {
        horizontalSwipe = true;
      }

      if (!horizontalSwipe) return;

      event.preventDefault();
      didDrag = true;

      if (target && (!dragState || dragState.direction !== target.direction)) {
        clearGalleryDragState(dragState);
        dragState = createGalleryDragState(gallery, deltaX);
      }

      if (dragState) setGalleryDragTransforms(dragState, deltaX);
    }

    function onPointerUp(event) {
      if (!tracking || event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      releasePointerCapture();

      if (absX >= SWIPE_MIN_DISTANCE && absX > absY * SWIPE_AXIS_RATIO) {
        event.preventDefault();
        suppressNextClick();
        if (dragState) {
          finishGalleryDrag(dragState, true);
        } else {
          const currentIndex = getActiveGalleryThumbIndex(gallery);
          const nextIndex = currentIndex + (deltaX < 0 ? 1 : -1);
          const thumbs = gallery.querySelectorAll('[data-gfs-gallery-thumb]');
          const wrappedIndex = ((nextIndex % thumbs.length) + thumbs.length) % thumbs.length;
          activateGalleryThumb(thumbs[wrappedIndex]);
        }
      } else if (dragState) {
        finishGalleryDrag(dragState, false);
        if (didDrag) suppressNextClick();
      }

      resetSwipe();
    }

    function onPointerCancel() {
      releasePointerCapture();
      if (dragState) finishGalleryDrag(dragState, false);
      resetSwipe();
    }

    function onClickCapture(event) {
      if (!suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }

    function onDragStart(event) {
      if (!isGallerySwipeEnabled(gallery)) return;
      if (isInteractiveGallerySwipeTarget(event.target)) return;

      event.preventDefault();
    }

    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('click', onClickCapture, true);
    stage.addEventListener('dragstart', onDragStart);
  }

  function bindGalleries() {
    document.querySelectorAll('[data-gfs-gallery]').forEach((gallery) => {
      const activeThumb = gallery.querySelector('[data-gfs-gallery-thumb][aria-current="true"]');
      if (activeThumb) {
        const thumbs = [...gallery.querySelectorAll('[data-gfs-gallery-thumb]')];
        gallery.dataset.gfsActiveIndex = String(thumbs.indexOf(activeThumb));
      }

      bindGallerySwipe(gallery);
      markGalleryStageImageState(gallery.querySelector('[data-gfs-gallery-stage]'));
    });
  }

  function openIngredientsDialog(button) {
    const targetId = button?.dataset.gfsIngredientsTarget;
    const dialog = targetId ? document.getElementById(targetId) : null;
    if (!dialog) return;

    dialog._gfsIngredientsOpener = button;
    dialog.classList.remove('is-closing', 'is-open');
    document.body.classList.add('gfs-ingredients-modal-open');

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    if (prefersReducedMotion()) {
      dialog.classList.add('is-open');
    } else {
      dialog.offsetHeight;
      window.requestAnimationFrame(() => {
        if (!dialog.open || dialog.classList.contains('is-closing')) return;
        dialog.classList.add('is-open');
      });
    }

    dialog.querySelector('[data-gfs-ingredients-close]')?.focus({ preventScroll: true });
  }

  function finishIngredientsDialogClose(dialog) {
    if (!dialog) return;

    dialog.classList.remove('is-closing', 'is-open');

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }

    document.body.classList.remove('gfs-ingredients-modal-open');

    if (dialog._gfsIngredientsOpener) {
      restoreFocusAfterModal(dialog._gfsIngredientsOpener);
      dialog._gfsIngredientsOpener = null;
    }
  }

  function closeIngredientsDialog(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;

    if (prefersReducedMotion()) {
      finishIngredientsDialogClose(dialog);
      return;
    }

    dialog.classList.remove('is-open');
    dialog.classList.add('is-closing');
    window.setTimeout(() => finishIngredientsDialogClose(dialog), INGREDIENTS_DIALOG_MS);
  }

  function setIngredientsAccordion(accordion, shouldOpen) {
    const trigger = accordion?.querySelector('[data-gfs-ingredients-trigger]');
    const panel = accordion?.querySelector('[data-gfs-ingredients-panel]');
    if (!trigger || !panel) return;

    trigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    panel.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');
    accordion.classList.toggle('is-open', shouldOpen);

    if (shouldOpen) {
      panel.hidden = false;
      panel.offsetHeight;
      window.requestAnimationFrame(() => panel.classList.add('is-open'));
      return;
    }

    panel.classList.remove('is-open');

    if (prefersReducedMotion()) {
      panel.hidden = true;
      return;
    }

    panel.addEventListener(
      'transitionend',
      (event) => {
        if (event.target !== panel || accordion.classList.contains('is-open')) return;
        panel.hidden = true;
      },
      { once: true }
    );
  }

  if (window.gigiPanelGalleryClickHandler) {
    document.removeEventListener('click', window.gigiPanelGalleryClickHandler);
  }

  window.gigiPanelGalleryClickHandler = (event) => {
    const target = event.target.nodeType === 1 ? event.target : event.target.parentElement;
    const thumb = target?.closest('[data-gfs-gallery-thumb]');
    if (thumb) {
      event.preventDefault();
      activateGalleryThumb(thumb);
      return;
    }

    const openButton = target?.closest('[data-gfs-ingredients-open]');
    if (openButton) {
      event.preventDefault();
      openIngredientsDialog(openButton);
      return;
    }

    const closeButton = target?.closest('[data-gfs-ingredients-close]');
    if (closeButton) {
      event.preventDefault();
      closeIngredientsDialog(closeButton.closest('[data-gfs-ingredients-dialog]'));
      return;
    }

    const trigger = target?.closest('[data-gfs-ingredients-trigger]');
    if (!trigger) return;

    const accordion = trigger.closest('[data-gfs-ingredients-accordion]');
    if (!accordion) return;

    event.preventDefault();
    setIngredientsAccordion(accordion, trigger.getAttribute('aria-expanded') !== 'true');
  };

  document.addEventListener('click', window.gigiPanelGalleryClickHandler);

  document.querySelectorAll('[data-gfs-ingredients-dialog]').forEach((dialog) => {
    if (dialog.dataset.gfsIngredientsBound === 'true') return;

    dialog.dataset.gfsIngredientsBound = 'true';

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeIngredientsDialog(dialog);
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeIngredientsDialog(dialog);
    });

    dialog.addEventListener('close', () => {
      dialog.classList.remove('is-closing', 'is-open');
      document.body.classList.remove('gfs-ingredients-modal-open');
    });
  });

  bindGalleries();
})();
